import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useDeviceAuth } from '../hooks/useDeviceAuth';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, Monitor } from 'lucide-react';

export default function ManualAuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { manualDeviceAuth, isLoading, error } = useDeviceAuth();
  const [userCode, setUserCode] = useState('');

  const handleManualAuth = async () => {
    if (!userCode.trim()) {
      return;
    }

    try {
      const result = await manualDeviceAuth(userCode.trim());
      if (result.access_token) {
        login(result.access_token);
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Manual auth failed:', err);
    }
  };

  const handleBack = () => {
    navigate('/auth');
  };

  const handleClose = () => {
    navigate('/welcome');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && userCode.trim() && !isLoading) {
      handleManualAuth();
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Main content - centered */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Monitor className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="flex items-center justify-center gap-2">
              <Monitor className="h-5 w-5" />
              Manual Authorization
            </CardTitle>
            <CardDescription>
              Enter the user code from your device authorization request:
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 bg-destructive/10">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="userCode">User Code</Label>
              <Input
                id="userCode"
                type="text"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                placeholder="e.g., WDJB-MJHT"
                className="font-mono text-center text-lg"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Enter the code exactly as shown on your authorization screen
              </p>
            </div>

            <Button 
              onClick={handleManualAuth} 
              className="w-full"
              disabled={!userCode.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Authorizing...
                </>
              ) : (
                'Authorize Device'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Bottom actions */}
      <div className="p-6 bg-white border-t">
        <div className="max-w-md mx-auto flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleBack}
            className="flex-1"
            disabled={isLoading}
          >
            Back
          </Button>
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="flex-1"
            disabled={isLoading}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}